import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsService, Document, ListDocumentsParams } from '@/services/api/documents';
import { permissionsService } from '@/services/api/permissions';
import { departmentsService } from '@/services/api/departments';
import { useDropzone } from 'react-dropzone';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - use unpkg CDN which is more reliable for pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Upload,
  Search,
  MoreHorizontal,
  Download,
  Trash2,
  RefreshCw,
  FileText,
  FileImage,
  FileSpreadsheet,
  File,
  Database,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Shield,
  Image,
  Lock,
  Users,
  Building2,
  Bot,
  X,
  AlertCircle,
  FolderOpen,
  HardDrive,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { DocumentPermissionsDialog } from './DocumentPermissionsDialog';

const FILE_ICONS: Record<string, React.ElementType> = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  txt: FileText,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
};

function getFileIcon(fileType: string) {
  return FILE_ICONS[fileType.toLowerCase()] || File;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface StatusBadgeProps {
  status: Document['bedrockStatus'];
  onRetry?: () => void;
  isRetrying?: boolean;
}

function StatusBadge({ status, onRetry, isRetrying }: StatusBadgeProps) {
  switch (status) {
    case 'indexed':
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Indexed
        </Badge>
      );
    case 'indexing':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Indexing
        </Badge>
      );
    case 'uploaded':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-600">
          <Upload className="mr-1 h-3 w-3" />
          Uploaded
        </Badge>
      );
    case 'failed':
      return (
        <div className="flex items-center gap-2">
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
          {onRetry && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Retry
                </>
              )}
            </Button>
          )}
        </div>
      );
    case 'pending':
    default:
      return (
        <Badge variant="outline">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
  }
}

// File upload types and helpers
const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const EXTENSION_TO_MIME: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc': 'application/msword',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
};

function getContentType(file: File): string {
  if (file.type) return file.type;
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return EXTENSION_TO_MIME[ext] || 'application/octet-stream';
}

type AccessType = 'private' | 'organization' | 'departments' | 'public_ai';

interface UploadingFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'confirming' | 'success' | 'error';
  error?: string;
  documentId?: string;
}

export function KnowledgeBasePage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [params, setParams] = useState<ListDocumentsParams>({
    limit: 20,
    offset: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Upload form state
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [accessType, setAccessType] = useState<AccessType>('private');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch documents
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['documents', params, searchQuery],
    queryFn: () => documentsService.listDocuments({ ...params, search: searchQuery }),
  });

  // Fetch KB status
  const { data: kbStatus } = useQuery({
    queryKey: ['kb-status'],
    queryFn: () => documentsService.getKBStatus(),
  });

  // Fetch departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.listDepartments(),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => documentsService.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['kb-status'] });
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    },
  });

  // Sync KB mutation
  const syncMutation = useMutation({
    mutationFn: () => documentsService.syncKnowledgeBase(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['kb-status'] });
    },
  });

  // Reprocess document mutation
  const [reprocessingDocId, setReprocessingDocId] = useState<string | null>(null);
  const [extractingImagesDocId, setExtractingImagesDocId] = useState<string | null>(null);
  const [imageViewerDoc, setImageViewerDoc] = useState<Document | null>(null);
  const [documentImages, setDocumentImages] = useState<Array<{
    id: string;
    pageNumber: number;
    imageIndex: number;
    s3Key: string;
    caption?: string;
    ocrText?: string;
    embeddingStatus: string;
    width?: number;
    height?: number;
    format?: string;
    imageType?: string;
  }>>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [pdfPageImages, setPdfPageImages] = useState<Record<number, string>>({});
  const [loadingPdfPages, setLoadingPdfPages] = useState(false);

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => {
      setReprocessingDocId(id);
      return documentsService.reprocessDocument(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['kb-status'] });
      setReprocessingDocId(null);
    },
    onError: () => {
      setReprocessingDocId(null);
    },
  });

  // Extract images mutation
  const extractImagesMutation = useMutation({
    mutationFn: (id: string) => {
      setExtractingImagesDocId(id);
      return documentsService.extractDocumentImages(id);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setExtractingImagesDocId(null);
      console.log('Image extraction completed:', result);
    },
    onError: (error) => {
      setExtractingImagesDocId(null);
      console.error('Image extraction failed:', error);
    },
  });

  // File dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const uploadFile = files[i];
      if (uploadFile.status !== 'pending') continue;

      try {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'uploading' as const, progress: 10 } : f
          )
        );

        const contentType = getContentType(uploadFile.file);
        const uploadData = await documentsService.getUploadUrl(
          uploadFile.file.name,
          contentType,
          uploadFile.file.size
        );

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, progress: 30, documentId: uploadData.documentId } : f
          )
        );

        await documentsService.uploadFile(uploadData.uploadUrl, uploadFile.file, contentType);

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, progress: 70, status: 'confirming' as const } : f
          )
        );

        await documentsService.confirmUpload(uploadData.documentId);

        // Set permissions based on access type
        if (accessType === 'organization') {
          await permissionsService.setPublicAccess(uploadData.documentId, {
            canView: true,
            canSearch: true,
            canEdit: false,
            canDelete: false,
            canDownload: true,
          });
        } else if (accessType === 'departments' && selectedDepartments.length > 0) {
          for (const deptId of selectedDepartments) {
            await permissionsService.setPermission(uploadData.documentId, {
              permissionType: 'department',
              departmentId: deptId,
              canView: true,
              canSearch: true,
              canEdit: false,
              canDelete: false,
              canDownload: true,
            });
          }
        } else if (accessType === 'public_ai') {
          await permissionsService.setPublicAccess(uploadData.documentId, {
            canView: true,
            canSearch: true,
            canEdit: false,
            canDelete: false,
            canDownload: false,
          });
        }

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, progress: 100, status: 'success' as const } : f
          )
        );
      } catch (error: any) {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: 'error' as const, error: error.message || 'Upload failed' }
              : f
          )
        );
      }
    }

    setIsUploading(false);
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    queryClient.invalidateQueries({ queryKey: ['kb-status'] });

    // Clear successful uploads after a delay
    setTimeout(() => {
      setFiles((prev) => prev.filter((f) => f.status !== 'success'));
    }, 2000);
  };

  const handleReprocess = (doc: Document) => {
    reprocessMutation.mutate(doc.id);
  };

  const handleExtractImages = (doc: Document) => {
    extractImagesMutation.mutate(doc.id);
  };

  const handleViewImages = async (doc: Document) => {
    setImageViewerDoc(doc);
    setLoadingImages(true);
    setPdfPageImages({});
    try {
      const result = await documentsService.getDocumentImages(doc.id);
      setDocumentImages(result.images || []);

      if (result.images && result.images.length > 0) {
        setLoadingPdfPages(true);
        try {
          const imageUrlsMap: Record<number, string> = {};
          for (const img of result.images) {
            try {
              const imageUrl = await documentsService.getImageUrl(doc.id, img.s3Key);
              const uniqueKey = img.imageIndex * 1000 + img.pageNumber;
              imageUrlsMap[uniqueKey] = imageUrl;
            } catch (imgError) {
              console.error(`Failed to get URL for image ${img.s3Key}:`, imgError);
            }
          }
          setPdfPageImages(imageUrlsMap);
        } catch (error) {
          console.error('Failed to load image URLs:', error);
        } finally {
          setLoadingPdfPages(false);
        }
      }
    } catch (error) {
      console.error('Failed to load images:', error);
      setDocumentImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setParams((prev) => ({ ...prev, offset: 0 }));
    refetch();
  }, [refetch]);

  const handleDownload = async (doc: Document) => {
    try {
      await documentsService.downloadDocument(doc.id, doc.originalFilename);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDelete = (doc: Document) => {
    setSelectedDocument(doc);
    setDeleteDialogOpen(true);
  };

  const handlePermissions = (doc: Document) => {
    setSelectedDocument(doc);
    setPermissionsDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedDocument) {
      deleteMutation.mutate(selectedDocument.id);
    }
  };

  const pendingCount = files.filter((f) => f.status === 'pending').length;

  const getAccessIcon = (type: AccessType) => {
    switch (type) {
      case 'private': return <Lock className="h-4 w-4" />;
      case 'organization': return <Users className="h-4 w-4" />;
      case 'departments': return <Building2 className="h-4 w-4" />;
      case 'public_ai': return <Bot className="h-4 w-4" />;
    }
  };

  const getAccessLabel = (type: AccessType) => {
    switch (type) {
      case 'private': return 'Private';
      case 'organization': return 'Organization';
      case 'departments': return 'Departments';
      case 'public_ai': return 'Public AI';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Knowledge Base</h1>
          <p className="text-sm text-muted-foreground">
            Upload and manage documents for AI-powered search
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Sync KB
        </Button>
      </div>

      {/* Upload Form - Inline */}
      <Card className="border-dashed">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`
                flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors min-h-[100px] flex flex-col items-center justify-center
                ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
              `}
            >
              <input {...getInputProps()} />
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? 'Drop files here...' : 'Drag & drop or click to upload'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, DOC, TXT, CSV, XLS (max 50MB)
              </p>
            </div>

            {/* Access Type and Upload Button */}
            <div className="flex flex-col gap-3 lg:w-[280px]">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Read Access</Label>
                <Select value={accessType} onValueChange={(v) => setAccessType(v as AccessType)}>
                  <SelectTrigger className="h-9">
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        {getAccessIcon(accessType)}
                        <span>{getAccessLabel(accessType)}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">
                      <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        <div>
                          <div className="font-medium">Private</div>
                          <div className="text-xs text-muted-foreground">Only you and admins</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="organization">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <div>
                          <div className="font-medium">Organization</div>
                          <div className="text-xs text-muted-foreground">All org users</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="departments">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <div>
                          <div className="font-medium">Departments</div>
                          <div className="text-xs text-muted-foreground">Selected departments</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="public_ai">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4" />
                        <div>
                          <div className="font-medium">Public AI</div>
                          <div className="text-xs text-muted-foreground">AI agents & visitors</div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Department Selection */}
              {accessType === 'departments' && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Select Departments</Label>
                  <div className="flex flex-wrap gap-1.5 max-h-[60px] overflow-y-auto">
                    {departmentsData?.departments?.map((dept) => (
                      <label
                        key={dept.id}
                        className={`
                          flex items-center gap-1.5 px-2 py-1 rounded-full border cursor-pointer text-xs transition-colors
                          ${selectedDepartments.includes(dept.id)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'hover:bg-muted'}
                        `}
                      >
                        <Checkbox
                          checked={selectedDepartments.includes(dept.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDepartments((prev) => [...prev, dept.id]);
                            } else {
                              setSelectedDepartments((prev) => prev.filter((id) => id !== dept.id));
                            }
                          }}
                          className="hidden"
                        />
                        {dept.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={uploadFiles}
                disabled={pendingCount === 0 || isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload {pendingCount > 0 ? `(${pendingCount})` : ''}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
              {files.map((uploadFile, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                >
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{uploadFile.file.name}</p>
                    {(uploadFile.status === 'uploading' || uploadFile.status === 'confirming') && (
                      <Progress value={uploadFile.progress} className="h-1 mt-1" />
                    )}
                    {uploadFile.status === 'error' && (
                      <p className="text-xs text-destructive truncate">{uploadFile.error}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {uploadFile.status === 'pending' && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(index)}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                    {(uploadFile.status === 'uploading' || uploadFile.status === 'confirming') && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                    {uploadFile.status === 'success' && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                    {uploadFile.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards - Compact with Icons */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <FolderOpen className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Documents</p>
              <p className="text-xl font-bold">{kbStatus?.documentCount ?? data?.total ?? 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Indexed</p>
              <p className="text-xl font-bold text-green-600">{kbStatus?.indexedCount ?? 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Database className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant={kbStatus?.configured ? 'default' : 'secondary'} className="mt-0.5">
                {kbStatus?.configured ? 'Active' : 'Setup'}
              </Badge>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <HardDrive className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Sync</p>
              <p className="text-sm font-medium truncate">
                {kbStatus?.lastSyncAt
                  ? formatDistanceToNow(new Date(kbStatus.lastSyncAt), { addSuffix: true })
                  : 'Never'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Documents Table */}
      <Card>
        <div className="p-4 border-b">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </div>
        <div className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-destructive">
              Failed to load documents. Please try again.
            </div>
          ) : data?.documents.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No documents yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Upload your first document using the form above
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Type</TableHead>
                    <TableHead className="hidden md:table-cell">Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Images</TableHead>
                    <TableHead className="hidden xl:table-cell">Topics</TableHead>
                    <TableHead className="hidden lg:table-cell">Uploaded</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.documents.map((doc) => {
                    const FileIcon = getFileIcon(doc.fileType);
                    return (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="truncate max-w-[150px] sm:max-w-[250px]">
                              {doc.originalFilename}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge variant="outline" className="uppercase text-xs">
                            {doc.fileType}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {formatFileSize(doc.fileSizeBytes)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={reprocessingDocId === doc.id ? 'indexing' : doc.bedrockStatus}
                            onRetry={doc.bedrockStatus === 'failed' && reprocessingDocId === null ? () => handleReprocess(doc) : undefined}
                            isRetrying={reprocessingDocId === doc.id}
                          />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {doc.fileType === 'pdf' ? (
                            extractingImagesDocId === doc.id ? (
                              <div className="flex items-center gap-1.5">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                <span className="text-xs text-muted-foreground">Extracting...</span>
                              </div>
                            ) : doc.imageExtractionStatus === 'processing' ? (
                              <div className="flex items-center gap-1.5">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                <span className="text-xs text-muted-foreground">Processing...</span>
                              </div>
                            ) : doc.imageExtractionStatus === 'completed' ? (
                              <button
                                className="flex items-center gap-1.5 hover:bg-accent rounded px-1.5 py-0.5 transition-colors cursor-pointer"
                                onClick={() => handleViewImages(doc)}
                              >
                                <Image className="h-4 w-4 text-green-500" />
                                <span className="text-sm font-medium">{doc.imageCount || 0}</span>
                              </button>
                            ) : doc.imageExtractionStatus === 'failed' ? (
                              <div className="flex items-center gap-1.5">
                                <XCircle className="h-4 w-4 text-destructive" />
                                <span className="text-xs text-destructive">Failed</span>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => handleExtractImages(doc)}
                                disabled={extractingImagesDocId !== null}
                              >
                                <Image className="mr-1 h-3 w-3" />
                                Extract
                              </Button>
                            )
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {reprocessingDocId === doc.id ? (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-xs">Extracting topics...</span>
                            </div>
                          ) : doc.mainTopic || (doc.topics && doc.topics.length > 0) ? (
                            <div className="flex flex-wrap gap-1 max-w-[280px]">
                              {(doc.mainTopic || doc.topics?.[0]) && (
                                <Badge
                                  variant="default"
                                  className="text-xs px-1.5 py-0 bg-primary text-primary-foreground font-medium"
                                >
                                  {doc.mainTopic || doc.topics?.[0]}
                                </Badge>
                              )}
                              {(doc.subTopics || doc.topics?.slice(1))?.slice(0, 2).map((topic, idx) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="text-xs px-1.5 py-0"
                                >
                                  {topic}
                                </Badge>
                              ))}
                              {((doc.subTopics?.length || 0) > 2 || ((doc.topics?.length || 1) - 1) > 2) && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <span className="inline-flex">
                                      <Badge variant="outline" className="text-xs px-1.5 py-0 cursor-pointer hover:bg-accent">
                                        +{(doc.subTopics?.length || (doc.topics?.length || 1) - 1) - 2}
                                      </Badge>
                                    </span>
                                  </PopoverTrigger>
                                  <PopoverContent side="top" className="w-auto p-3">
                                    <div className="space-y-2 max-w-[300px]">
                                      <div>
                                        <span className="text-xs text-muted-foreground font-medium">Main Topic:</span>
                                        <div className="mt-1">
                                          <Badge
                                            variant="default"
                                            className="text-xs bg-primary text-primary-foreground font-medium"
                                          >
                                            {doc.mainTopic || doc.topics?.[0]}
                                          </Badge>
                                        </div>
                                      </div>
                                      {((doc.subTopics?.length || 0) > 0 || ((doc.topics?.length || 1) - 1) > 0) && (
                                        <div>
                                          <span className="text-xs text-muted-foreground font-medium">
                                            Sub-topics ({doc.subTopics?.length || (doc.topics?.length || 1) - 1}):
                                          </span>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {(doc.subTopics || doc.topics?.slice(1))?.map((topic, idx) => (
                                              <Badge
                                                key={idx}
                                                variant="secondary"
                                                className="text-xs"
                                              >
                                                {topic}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
                          {formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePermissions(doc)}>
                                <Shield className="mr-2 h-4 w-4" />
                                Permissions
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleReprocess(doc)}
                                disabled={reprocessingDocId !== null}
                              >
                                {reprocessingDocId === doc.id ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="mr-2 h-4 w-4" />
                                )}
                                {reprocessingDocId === doc.id ? 'Re-indexing...' : 'Re-index'}
                              </DropdownMenuItem>
                              {doc.fileType === 'pdf' && (
                                <DropdownMenuItem
                                  onClick={() => handleExtractImages(doc)}
                                  disabled={extractingImagesDocId !== null}
                                >
                                  {extractingImagesDocId === doc.id ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : (
                                    <Image className="mr-2 h-4 w-4" />
                                  )}
                                  {extractingImagesDocId === doc.id ? 'Extracting...' : 'Extract Images'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                <Download className="mr-2 h-4 w-4" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(doc)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {data && data.total > params.limit! && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {params.offset! + 1} to{' '}
                {Math.min(params.offset! + params.limit!, data.total)} of {data.total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={params.offset === 0}
                  onClick={() =>
                    setParams((prev) => ({
                      ...prev,
                      offset: Math.max(0, prev.offset! - prev.limit!),
                    }))
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={params.offset! + params.limit! >= data.total}
                  onClick={() =>
                    setParams((prev) => ({
                      ...prev,
                      offset: prev.offset! + prev.limit!,
                    }))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedDocument?.originalFilename}"? This action
              cannot be undone and will also remove the document from the vector database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permissions Dialog */}
      <DocumentPermissionsDialog
        open={permissionsDialogOpen}
        onOpenChange={setPermissionsDialogOpen}
        document={selectedDocument}
      />

      {/* Image Viewer Dialog */}
      <Dialog open={imageViewerDoc !== null} onOpenChange={(open) => !open && setImageViewerDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh]" aria-describedby="image-viewer-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-green-500" />
              Extracted Images - {imageViewerDoc?.originalFilename}
              <Badge variant="secondary" className="ml-2">{documentImages.length} images</Badge>
            </DialogTitle>
            <p id="image-viewer-description" className="sr-only">
              View extracted images from the document
            </p>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[65vh]">
            {loadingImages ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading images...</span>
              </div>
            ) : documentImages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No images found in this document.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {documentImages.map((img) => (
                  <div
                    key={img.id}
                    className="border rounded-lg overflow-hidden flex flex-col"
                  >
                    <div className="bg-muted/30 p-2 flex-1 flex items-center justify-center min-h-[150px]">
                      {loadingPdfPages ? (
                        <div className="flex flex-col items-center justify-center h-32">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          <span className="mt-1 text-xs text-muted-foreground">Loading...</span>
                        </div>
                      ) : pdfPageImages[img.imageIndex * 1000 + img.pageNumber] ? (
                        <img
                          src={pdfPageImages[img.imageIndex * 1000 + img.pageNumber]}
                          alt={`Extracted image from page ${img.pageNumber}`}
                          className="max-w-full h-auto rounded border shadow-sm"
                          style={{ maxHeight: '200px', objectFit: 'contain' }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
                          <FileImage className="h-6 w-6" />
                          <span className="mt-1 text-xs">Not available</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2 border-t bg-background">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="default" className="text-xs px-1.5 py-0 bg-primary">
                            Page {img.pageNumber}
                          </Badge>
                          {img.imageIndex > 0 && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              #{img.imageIndex + 1}
                            </Badge>
                          )}
                        </div>
                        {img.width && img.height && (
                          <span className="text-[10px] text-muted-foreground">
                            {img.width}×{img.height}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-3">
                        {img.caption || 'No description available'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
            <p>These images are indexed for semantic search.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
